import { useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { addInterceptor, Interceptor, PASS, removeInterceptor } from '@/api/mutator/fetchInterceptors';
import { ROUTE } from '@/constants/route';
import { log } from '@/utils/functions/logger/logger';

import { DEMO_STORAGE_KEY, DemoContext, getDemoMode } from './DemoContext';

import type { DemoContextType } from './DemoContext';
import type { ReactNode } from 'react';

const demoInterceptor: Interceptor = async ({ url }) => {
  const { getDemoApiMapping } = await import('@/utils/functions/getDemoApiMapping/getDemoApiMapping');
  const fixture = getDemoApiMapping(url);
  if (fixture !== null) return fixture;
  if (import.meta.env.DEV) {
    log.warn(`[DemoInterceptor] No fixture mapped for URL: ${url}`);
  }
  return PASS;
};

const DemoProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [isDemo, setIsDemo] = useState<boolean>(() => {
    const fromStorage = getDemoMode();
    const hasUrlParam = new URLSearchParams(globalThis.location.search).get('isDemo') === 'true';
    if (hasUrlParam) localStorage.setItem(DEMO_STORAGE_KEY, 'true');
    const active = fromStorage || hasUrlParam;
    if (active) addInterceptor(demoInterceptor);
    return active;
  });

  const activateDemo = () => {
    localStorage.setItem(DEMO_STORAGE_KEY, 'true');
    addInterceptor(demoInterceptor);
    setIsDemo(true);
    navigate(ROUTE.DASHBOARD, { replace: true });
  };

  const deactivateDemo = () => {
    localStorage.removeItem(DEMO_STORAGE_KEY);
    removeInterceptor(demoInterceptor);
    setIsDemo(false);
  };

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get('isDemo') !== 'true') return;
    navigate(ROUTE.DASHBOARD, { replace: true });
  }, [navigate]);

  const contextValue = useRef<DemoContextType>({ isDemo, activateDemo, deactivateDemo }).current;
  contextValue.isDemo = isDemo;

  return <DemoContext.Provider value={contextValue}>{children}</DemoContext.Provider>;
};

export default DemoProvider;
